import { Loader2, ShieldCheck, ShieldOff } from 'lucide-react';
import { useState } from 'react';
import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/hooks/use-auth';
import { authApi } from '@/lib/auth-api';
import { getErrorMessage, getFieldErrors } from '@/lib/errors';
import type { TwoFactorSetup } from '@/types/api';

export function SecurityPage() {
  const { user, refreshUser } = useAuth();

  return (
    <div className="space-y-6">
      <ChangePasswordCard />
      <TwoFactorCard enabled={user?.two_factor_enabled ?? false} onChanged={refreshUser} />
    </div>
  );
}

function ChangePasswordCard() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [status, setStatus] = useState<{ type: 'ok' | 'error'; message: string } | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setStatus(null);
    setFieldErrors({});

    if (next !== confirm) {
      setFieldErrors({ new_password: 'Passwords do not match.' });
      return;
    }

    setLoading(true);
    try {
      const res = await authApi.changePassword(current, next);
      setStatus({ type: 'ok', message: res.message });
      setCurrent('');
      setNext('');
      setConfirm('');
    } catch (err) {
      setStatus({ type: 'error', message: getErrorMessage(err) });
      setFieldErrors(getFieldErrors(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Password</CardTitle>
        <CardDescription>
          Changing your password signs out all of your other devices.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {status && (
          <Alert variant={status.type === 'ok' ? 'success' : 'danger'} className="mb-4">
            {status.message}
          </Alert>
        )}
        <form onSubmit={onSubmit} className="max-w-sm space-y-4">
          <div className="space-y-2">
            <Label htmlFor="current">Current password</Label>
            <Input id="current" type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} required />
          </div>
          <div className="space-y-2">
            <Label htmlFor="next">New password</Label>
            <Input id="next" type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} required />
            {fieldErrors.new_password ? (
              <p className="text-xs text-danger">{fieldErrors.new_password}</p>
            ) : (
              <p className="text-xs text-muted-foreground">
                At least 12 characters, with upper, lower, and a number.
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirm">Confirm new password</Label>
            <Input id="confirm" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
          </div>
          <Button type="submit" disabled={loading}>
            {loading && <Loader2 className="animate-spin" />} Update password
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function TwoFactorCard({ enabled, onChanged }: { enabled: boolean; onChanged: () => Promise<void> }) {
  const [setup, setSetup] = useState<TwoFactorSetup | null>(null);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [status, setStatus] = useState<{ type: 'ok' | 'error'; message: string } | null>(null);
  const [loading, setLoading] = useState(false);

  async function begin() {
    setStatus(null);
    setLoading(true);
    try {
      setSetup(await authApi.enableTwoFactor());
    } catch (err) {
      setStatus({ type: 'error', message: getErrorMessage(err) });
    } finally {
      setLoading(false);
    }
  }

  async function confirm(e: React.FormEvent) {
    e.preventDefault();
    setStatus(null);
    setLoading(true);
    try {
      const res = await authApi.confirmTwoFactor(code);
      setSetup(null);
      setCode('');
      setStatus({ type: 'ok', message: res.message });
      await onChanged();
    } catch (err) {
      setStatus({ type: 'error', message: getErrorMessage(err) });
    } finally {
      setLoading(false);
    }
  }

  async function disable(e: React.FormEvent) {
    e.preventDefault();
    setStatus(null);
    setLoading(true);
    try {
      const res = await authApi.disableTwoFactor(password);
      setPassword('');
      setStatus({ type: 'ok', message: res.message });
      await onChanged();
    } catch (err) {
      setStatus({ type: 'error', message: getErrorMessage(err) });
    } finally {
      setLoading(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <div>
            <CardTitle>Two-factor authentication</CardTitle>
            <CardDescription>
              Require a time-based code from your authenticator app when signing in.
            </CardDescription>
          </div>
          <Badge variant={enabled ? 'success' : 'muted'}>
            {enabled ? (
              <>
                <ShieldCheck className="mr-1 size-3" /> Enabled
              </>
            ) : (
              <>
                <ShieldOff className="mr-1 size-3" /> Disabled
              </>
            )}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        {status && (
          <Alert variant={status.type === 'ok' ? 'success' : 'danger'} className="mb-4">
            {status.message}
          </Alert>
        )}

        {enabled ? (
          <form onSubmit={disable} className="max-w-sm space-y-4">
            <div className="space-y-2">
              <Label htmlFor="disable-password">Confirm your password to disable</Label>
              <Input id="disable-password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
            </div>
            <Button type="submit" variant="danger" disabled={loading}>
              {loading && <Loader2 className="animate-spin" />} Disable two-factor
            </Button>
          </form>
        ) : setup ? (
          <div className="space-y-4">
            <div className="rounded-md border border-border bg-muted/20 p-4">
              <p className="text-sm text-muted-foreground">
                Add this secret to your authenticator app, then enter the generated code to confirm.
              </p>
              <p className="mt-3 select-all break-all font-mono text-sm">{setup.secret}</p>
              <a
                href={setup.otpauth_uri}
                className="mt-2 inline-block text-xs text-primary hover:underline"
              >
                Open in authenticator app
              </a>
            </div>
            <form onSubmit={confirm} className="max-w-sm space-y-4">
              <div className="space-y-2">
                <Label htmlFor="totp">Authentication code</Label>
                <Input id="totp" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(e) => setCode(e.target.value)} required />
              </div>
              <div className="flex gap-2">
                <Button type="submit" disabled={loading}>
                  {loading && <Loader2 className="animate-spin" />} Confirm & enable
                </Button>
                <Button type="button" variant="ghost" onClick={() => setSetup(null)}>
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        ) : (
          <Button onClick={begin} disabled={loading}>
            {loading && <Loader2 className="animate-spin" />} Set up two-factor
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
